/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl-next';
import { IntlProvider } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { type Schema, type Slice, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';

const buttonContainer = css({
	position: 'absolute',
	top: 0,
	right: 0,
	zIndex: 1,
	transform: 'translateX(100%)',
});

function createSliceFromToolCallValue(schema: Schema, value: string): Slice | null {
	try {
		const parsed = typeof value === 'string' ? JSON.parse(value) : value;
		const node = processRawValue(schema, parsed);
		if (!node || node.content.size === 0) {
			return null;
		}
		const slice = node.slice(0, node.content.size);
		return slice;
	} catch {
		return null;
	}
}

const getInversion = (tr: Transaction, suggestion: Suggestion) => {
	const localIdToPos = new Map<string, { from: number; to: number }>();
	tr.doc.descendants((node, pos) => {
		const localId = node.attrs?.localId;
		if (localId) {
			localIdToPos.set(localId, { from: pos, to: pos + node.nodeSize });
		}
		return false;
	});
	/**
	 * For some reason, without transforming the doc back and forth,
	 * the steps are missing in subsequent transforms.
	 */
	const transform = new Transform(PMNode.fromJSON(tr.doc.type.schema, tr.doc.toJSON()));
	suggestion.toolCalls.forEach((toolCall) => {
		const localId = toolCall.arguments?.localId;
		const value = toolCall.arguments?.value;
		if (localId === undefined || value === undefined) {
			return;
		}
		const slice = createSliceFromToolCallValue(tr.doc.type.schema, value);
		if (!slice) {
			return;
		}
		const pos = localIdToPos.get(localId);
		if (!pos) {
			return;
		}

		switch (toolCall.name) {
			case 'insertNodeAfter':
				transform.insert(pos.to, slice.content);
				break;
			case 'replaceNode':
				transform.replaceRange(pos.from, pos.to, slice);
				break;
			default:
				break;
		}
	});
	return transform;
};

export function SuggestionIconButton({
	suggestion,
	selectedSuggestion,
	api,
	intl,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	selectedSuggestion?: string;
	suggestion: Suggestion;
}) {
	const isSelected = selectedSuggestion === suggestion.id;
	const onSelectSuggestion = () => {
		api?.core?.actions.execute(({ tr }) => {
			if (isSelected) {
				api?.showDiff?.commands.hideDiff({ tr });
			} else {
				const invertedTr = getInversion(tr, suggestion);
				api?.showDiff?.commands.showDiff({
					steps: invertedTr.steps.map((step) => step.invert(invertedTr.before)),
					originalDoc: invertedTr.doc,
					isInverted: true,
				})({ tr });
			}

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: isSelected ? undefined : suggestion.id,
			});
			return tr;
		});
	};

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={buttonContainer}
				style={{
					anchorName: `--button-${suggestion.id}`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && (
						<IconButton
							icon={AiGenerativeTextIcon}
							appearance="subtle"
							label=""
							spacing="compact"
							onClick={onSelectSuggestion}
						/>
					)
				}
			</span>
		</IntlProvider>
	);
}
